import React, { useState, useEffect, useMemo } from 'react'
import cx from 'classnames'
import { find } from 'lodash'
import jump from 'jump.js'

import ArticlesDesc from 'static/articles.json'
import artilceTopics from 'static/editorial-topics.json'

import { EditorialCard } from 'src/components/editorial/editorialCard'
import { SingleSelector } from 'src/components/singleSelector'

const INITIAL_ARTICLES = 18
const ARTICLES_PER_PAGE = 9

const topicsTitle = [
  { id: 'all', title: 'All Topics', available: true, type: 'topic' },
]
const topics = topicsTitle.concat(artilceTopics)

const defaultSortBy = [
  { id: 'latest', title: 'Latest Articles', available: true, type: 'sort' },
  { id: 'oldest', title: 'Oldest Articles', available: true, type: 'sort' },
]

const handleFilter = (filter: string, callBack: any) => {
  switch (filter) {
    case 'topics':
      return <SingleSelector options={topics} handleSelection={callBack} />
    case 'sort':
      return <SingleSelector options={defaultSortBy} handleSelection={callBack} />
    default:
      return null
  }
}

const toValidArticles = (article: any) => {
  return article && article.content && article.content.main
}
const byArticleTopic = (topic: string) => (article: any) => {
  if ('All Topics'.localeCompare(topic, 'en', { sensitivity: 'base' }) === 0) {
    return article
  } else {
    return find(article.content.main.topics, { title: topic })
  }
}

const getArticleSortDate = (article: any) => {
  const { updatedAt, publishedAt } = article?.content?.main || {}
  const value = updatedAt || publishedAt
  const time = value ? Date.parse(value) : 0

  return Number.isNaN(time) ? 0 : time
}

const sortArticlesByDate = (sort: string) => (a: any, b: any) => {
  const diff = getArticleSortDate(b) - getArticleSortDate(a)

  return sort === 'oldest' ? -diff : diff
}

const articleSearchText = (article: any) => {
  const main = article?.content?.main || {}
  const topics = main.topics?.map(topic => topic?.title).join(' ')
  const authors = main.authors
    ?.map(author => author?.content?.main?.title)
    .join(' ')

  return [main.title, main.articleType?.title, topics, authors]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

const bySearchQuery = (query: string) => (article: any) => {
  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery) {
    return true
  }

  return articleSearchText(article).includes(normalizedQuery)
}

export interface EditorialGridProps {
  data: {
    backgroundColor: string
    filters?: any[]
    spacing: string
  }
}

export const EditorialGrid = ({ data }: EditorialGridProps) => {
  const [topic, setTopic] = useState('All Topics')
  const [sort, setSort] = useState('latest')
  const [searchQuery, setSearchQuery] = useState('')
  const [show, setShow] = useState(INITIAL_ARTICLES)
  const {
    spacing,
    title,
    filters = [],
    backgroundColor,
  } = data
  const enabledFilters = (filters || []).filter(filter => filter === 'topics')

  const callBack = e => {
    switch (e.type) {
      case 'sort':
        setSort(e.id)
        break
      case 'topic':
        setTopic(e.title)
        break
      default:
        null
        break
    }
  }

  const stateArticles = useMemo(() => {
    return [...ArticlesDesc]
      .sort(sortArticlesByDate(sort))
      .filter(toValidArticles)
      .filter(byArticleTopic(topic))
      .filter(bySearchQuery(searchQuery))
  }, [sort, topic, searchQuery])

  useEffect(() => {
    setShow(INITIAL_ARTICLES)
  }, [sort, topic, searchQuery])

  const showMore = e => {
    e.preventDefault()
    setShow(show + ARTICLES_PER_PAGE)
    jump(0, {
      duration: 0,
    })
  }

  return (
    <div
      className={cx('editorial__grid', backgroundColor, spacing, {
        cw: backgroundColor === 'bcb',
      })}
    >
      <div className="container--l editorial__filters cw x outer mxa pys p1 al rel">
        {title && <h2 className="h1 mt0 x module-title-serif">{title}</h2>}
        <div className="editorial__filters-controls">
          <div className="editorial__filters-control editorial__filters-search">
            <span>Search:</span>
            <input
              className="editorial__search-input"
              type="search"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder="Search articles"
              aria-label="Search articles"
            />
          </div>
          {enabledFilters.length > 0 && (
            <div className="editorial__filters-control">
              <span>Filter by:</span>
              {enabledFilters.map((filter, i) => (
                <div key={i} className="editorial__filters-field">
                  {handleFilter(filter, callBack)}
                </div>
              ))}
            </div>
          )}
          <div className="editorial__filters-control">
            <span>Sort by:</span>
            <div className="editorial__filters-field">
              {handleFilter('sort', callBack)}
            </div>
          </div>
        </div>
      </div>
      <div className="container--l editorial__grid-list outer mxa pys p1 al x rel">
        {stateArticles?.slice(0, show)?.map((article: any, i: number) => {
          return (
            <EditorialCard
              key={article._id || i}
              fromArticle
              className="editorial__grid-card"
              {...(article?.content?.main || {})}
            />
          )
        })}

        {stateArticles.length === 0 && (
          <div className="x container--s">
            <h3 className="karla">
              We couldn’t find what you’re looking for.
              <br />
              Try another search.
            </h3>
          </div>
        )}
      </div>
      <div className="container--l outer mxa pb1 f jcc pym">
        {stateArticles.length > show && (
          <a onClick={e => showMore(e)} className="button  cursor no-underline">
            <span className="z1 rel">More</span>
          </a>
        )}
      </div>
    </div>
  )
}

export default EditorialGrid
